import React, { useEffect, useState } from 'react';

/**
 * HelpfulStatBadge — May 6, 2026
 *
 * Public aggregated trust badge: "Helpful: NN% · n votes". Sourced from
 * /api/feedback/public-stats which only returns the aggregate numbers
 * (positive/negative counts, %, total) — no emails, no record_ids, no
 * reasons. Privacy posture documented in server.py.
 *
 * Hard min-votes threshold of 20 (server-enforced). When the threshold
 * isn't met, the endpoint returns qualifies=false and this component
 * renders nothing — better to be invisible on day 1 than to ship a noisy
 * stat ("Helpful: 100%, n=2") that we'd have to walk back.
 *
 * Two visual variants via the `variant` prop:
 *   • 'card' (default) — full pill card for hero / testimonial bands
 *   • 'inline'         — compact pill for trust strips next to other badges
 *
 * Cached at the module level with a 60s TTL so multiple mounts on the
 * same page (homepage testimonial band + footer microcopy, e.g.) hit
 * the network only once.
 */

let _cache = null;
let _cacheAt = 0;
const TTL_MS = 60 * 1000;

async function fetchStats() {
  const now = Date.now();
  if (_cache && now - _cacheAt < TTL_MS) return _cache;
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback/public-stats?days=30`);
    if (!res.ok) return null;
    const data = await res.json();
    _cache = data;
    _cacheAt = now;
    return data;
  } catch {
    return null;
  }
}

export default function HelpfulStatBadge({ variant = 'card', className = '' }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchStats().then((data) => { if (mounted) setStats(data); });
    return () => { mounted = false; };
  }, []);

  if (!stats || !stats.qualifies) return null;

  const pct = stats.positive_rate_pct;
  const n = stats.total_votes;

  if (variant === 'inline') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FBF9F6] border border-[#E8E2D9] text-xs text-[#6B635E] ${className}`}
        data-testid="helpful-stat-inline"
        title={`Based on ${n} member votes in the last 30 days`}
      >
        <span aria-hidden="true">👍</span>
        <span className="text-[#2D2825] font-semibold">Helpful: {pct}%</span>
        <span className="text-[#6B635E]/70">· {n} votes (30d)</span>
      </span>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-3 px-5 py-3 rounded-full bg-white border border-[#E8E2D9] shadow-sm ${className}`}
      data-testid="helpful-stat-card"
      role="status"
      aria-label={`Helpful rating: ${pct} percent based on ${n} member votes in the last 30 days`}
    >
      <span className="text-2xl" aria-hidden="true">👍</span>
      <div className="text-left">
        <div className="flex items-baseline gap-1.5">
          <span className="text-[#D0495A] font-bold text-xl leading-none">{pct}%</span>
          <span className="text-[#2D2825] font-medium text-sm">helpful</span>
        </div>
        <p className="text-[#6B635E] text-[11px] mt-0.5">
          Member-rated · {n} votes · last 30 days
        </p>
      </div>
    </div>
  );
}
